import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BrandMark from '../components/BrandMark';
import CountryPicker from '../components/CountryPicker';
import DobPicker from '../components/DobPicker';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import { useAuth } from '../context/AuthContext';
import CaptchaGate, { HoneypotField } from '../components/CaptchaGate';
import { authApi } from '../api/authApi';
import { isStrongPassword } from '../utils/passwordStrength';
import { isGdprCountry } from '../utils/countries';

// Full-spec registration. Real-time username + email availability
// checks, password strength feedback, country picker, DOB picker,
// and a GDPR consent block when the user picks an EEA / UK country.
export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    username: '', displayName: '', email: '', password: '', confirmPassword: '',
    country: '', dob: '', legalAccepted: false,
    marketingEmails: false, analytics: true
  });
  const [usernameState, setUsernameState] = useState({ status: 'idle', message: '' });
  const [emailState, setEmailState] = useState({ status: 'idle', message: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const usernameDebounce = useRef(null);
  const emailDebounce = useRef(null);

  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const [captchaToken, setCaptchaToken] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const onInput = (k) => (e) => set(k)(e.target.type === 'checkbox' ? e.target.checked : e.target.value);

  // Username availability (debounced)
  useEffect(() => {
    if (!form.username) { setUsernameState({ status: 'idle', message: '' }); return; }
    if (!/^[a-zA-Z0-9_]{3,30}$/.test(form.username)) {
      setUsernameState({ status: 'error', message: '3–30 chars, letters / numbers / underscore.' });
      return;
    }
    setUsernameState({ status: 'checking', message: 'Checking…' });
    clearTimeout(usernameDebounce.current);
    usernameDebounce.current = setTimeout(async () => {
      try {
        const data = await authApi.usernameAvailability(form.username);
        if (data?.available) setUsernameState({ status: 'ok', message: 'Available ✓' });
        else setUsernameState({ status: 'error', message: data?.reason || 'Already taken' });
      } catch (err) {
        setUsernameState({ status: 'error', message: err.message || 'Check failed' });
      }
    }, 350);
    return () => clearTimeout(usernameDebounce.current);
  }, [form.username]);

  // Email availability (debounced)
  useEffect(() => {
    if (!form.email || !form.email.includes('@')) { setEmailState({ status: 'idle', message: '' }); return; }
    setEmailState({ status: 'checking', message: 'Checking…' });
    clearTimeout(emailDebounce.current);
    emailDebounce.current = setTimeout(async () => {
      try {
        const data = await authApi.emailAvailability(form.email);
        if (data?.available) setEmailState({ status: 'ok', message: 'Available ✓' });
        else setEmailState({ status: 'error', message: 'Already registered' });
      } catch (err) {
        setEmailState({ status: 'error', message: err.message || 'Check failed' });
      }
    }, 400);
    return () => clearTimeout(emailDebounce.current);
  }, [form.email]);

  const gdpr = isGdprCountry(form.country);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.legalAccepted) { setError('Accept the terms to continue.'); return; }
    if (!isStrongPassword(form.password)) { setError('Pick a stronger password.'); return; }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match.'); return; }
    if (usernameState.status === 'error') { setError('Fix the username first.'); return; }
    if (emailState.status === 'error') { setError('Fix the email first.'); return; }
    if (!form.dob) { setError('Please enter your date of birth.'); return; }
    if (!form.country) { setError('Pick your country.'); return; }
    setBusy(true);
    try {
      // Strip the confirm field — it's a client-side gate only;
      // the server validator rejects unknown fields.
      const { confirmPassword, ...payload } = form;
      await register({
        ...payload,
        username: payload.username.trim(),
        email: payload.email.trim(),
        // Bot-protection fields. Both are stripped server-side before
        // the controller runs; harmless no-ops when it is not enabled.
        captchaToken,
        nickname: honeypot
      });
      navigate('/welcome', { replace: true });
    } catch (err) {
      setError(err.message || 'Sign-up failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <Link to="/" style={{ marginBottom: 24, display: 'inline-block' }}><BrandMark size={28} /></Link>
        <h2 className="h2">Get on the board.</h2>
        <p className="muted" style={{ marginBottom: 18 }}>Free account. About a minute to set up.</p>

        <form onSubmit={submit}>
          <div className="row" style={{ gap: 10 }}>
            <div className="form-row" style={{ flex: 1 }}>
              <label className="label">Username</label>
              <input
                value={form.username}
                onChange={onInput('username')}
                placeholder="yourname"
                maxLength={30}
                style={{
                  borderColor:
                    usernameState.status === 'ok' ? 'var(--success)' :
                    usernameState.status === 'error' ? 'var(--danger)' : undefined
                }}
              />
              <span style={{
                fontSize: 12, fontWeight: 800,
                color: usernameState.status === 'ok' ? 'var(--success)' :
                       usernameState.status === 'error' ? 'var(--danger)' :
                       'var(--muted)'
              }}>{usernameState.message || ' '}</span>
            </div>
            <div className="form-row" style={{ flex: 1 }}>
              <label className="label">Display name</label>
              <input required maxLength={60} value={form.displayName} onChange={onInput('displayName')} placeholder="Your Name" />
            </div>
          </div>

          <div className="form-row">
            <label className="label">Email</label>
            <input
              type="email" required value={form.email} onChange={onInput('email')}
              style={{
                borderColor:
                  emailState.status === 'ok' ? 'var(--success)' :
                  emailState.status === 'error' ? 'var(--danger)' : undefined
              }}
            />
            <span style={{
              fontSize: 12, fontWeight: 800,
              color: emailState.status === 'ok' ? 'var(--success)' :
                     emailState.status === 'error' ? 'var(--danger)' :
                     'var(--muted)'
            }}>{emailState.message || ' '}</span>
          </div>

          <div className="form-row">
            <label className="label">Password</label>
            <input type="password" required minLength={8} maxLength={128} autoComplete="new-password" value={form.password} onChange={onInput('password')} />
          </div>

          <div className="form-row">
            <label className="label">Confirm password</label>
            <input
              type="password" required minLength={8} maxLength={128}
              autoComplete="new-password"
              value={form.confirmPassword} onChange={onInput('confirmPassword')}
              style={{
                borderColor:
                  form.confirmPassword && form.confirmPassword !== form.password ? 'var(--danger)' :
                  form.confirmPassword && form.confirmPassword === form.password ? 'var(--success)' : undefined
              }}
            />
            {/* Strength meter is wired with both fields so it shows
                strength + a live "passwords match" indicator. */}
            <PasswordStrengthMeter password={form.password} confirmPassword={form.confirmPassword} />
          </div>

          <div className="form-row">
            <label className="label">Country</label>
            <CountryPicker value={form.country} onChange={set('country')} />
          </div>

          <div className="form-row">
            <label className="label">Date of birth</label>
            <DobPicker value={form.dob} onChange={set('dob')} />
            <span className="hint">You must be at least 13.</span>
          </div>

          <label className="row" style={{ alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
            <input type="checkbox" checked={form.legalAccepted} onChange={onInput('legalAccepted')} style={{ width: 16, height: 16, marginTop: 3 }} />
            <span className="muted" style={{ fontSize: 12 }}>
              I agree to the <a href="/legal/terms" target="_blank" rel="noreferrer">Terms of Service</a> and <a href="/legal/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
            </span>
          </label>

          {gdpr ? (
            <div className="card-soft" style={{ marginBottom: 12, padding: 12 }}>
              <p className="eyebrow" style={{ color: 'var(--accent)', marginBottom: 6 }}>YOUR GDPR RIGHTS</p>
              <p className="muted" style={{ marginTop: 0, marginBottom: 10, fontSize: 12 }}>
                You're in a country with GDPR-equivalent rights. These consents are optional and can be changed anytime in Settings → Privacy.
              </p>
              <label className="row" style={{ alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
                <input type="checkbox" checked={form.marketingEmails} onChange={onInput('marketingEmails')} style={{ width: 16, height: 16, marginTop: 3 }} />
                <span style={{ fontSize: 12 }}>Send me product updates and announcements.</span>
              </label>
              <label className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
                <input type="checkbox" checked={form.analytics} onChange={onInput('analytics')} style={{ width: 16, height: 16, marginTop: 3 }} />
                <span style={{ fontSize: 12 }}>Anonymous analytics to help us improve the product.</span>
              </label>
            </div>
          ) : (
            <label className="row" style={{ alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
              <input type="checkbox" checked={form.marketingEmails} onChange={onInput('marketingEmails')} style={{ width: 16, height: 16, marginTop: 3 }} />
              <span className="muted" style={{ fontSize: 12 }}>Email me product updates (optional).</span>
            </label>
          )}

          <HoneypotField value={honeypot} onChange={setHoneypot} />
          <CaptchaGate onToken={setCaptchaToken} />
          {error ? <p className="error-text">{error}</p> : null}
          <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={busy}>
            {busy ? 'Creating account…' : 'Create account →'}
          </button>
        </form>

        <p className="row" style={{ justifyContent: 'center', marginTop: 16, fontSize: 13 }}>
          <span className="muted">Already have one?&nbsp;</span><Link to="/login">Sign in</Link>
        </p>
      </div>

      <style>{`
        .auth-shell {
          min-height: 100vh;
          display: grid; place-items: center;
          padding: 32px 16px;
          background:
            radial-gradient(circle at 0% 0%, rgba(255,62,127,.16), transparent 35%),
            radial-gradient(circle at 100% 100%, rgba(91,233,255,.10), transparent 40%),
            var(--bg);
        }
        .auth-card {
          width: min(540px, 100%);
          background: var(--panel);
          border: 1px solid var(--line);
          border-radius: var(--radius-lg);
          padding: 28px;
          box-shadow: var(--shadow-card);
        }
      `}</style>
    </div>
  );
}
